import { buildConventionalScopeBody, ConventionalScope, conventionalScopeKey, coreApi, BlueprintFamilyTrendsWindowDays } from '../core'
import type { BlueprintFamilyDurationDistributionApiItem } from './durationDistribution'

// Response shape for POST /blueprint-families/duration-distribution/event-groups: the
// per-family buckets, wrapped per event-group id. The histogram is group-scoped — every
// game running any event of the group — so a game that ran two of the group's families
// is counted once per bucket, not once per family.
//
// The same overlap caveat as the per-family endpoint applies within a row: a game counts
// in EVERY bucket it ran an event in, so the buckets are not a partition and their counts
// must not be summed into a denominator. See `durationDistribution.ts`.
export type BlueprintFamilyEventGroupDurationDistributionApiItem = {
  id: string
  buckets: BlueprintFamilyDurationDistributionApiItem[]
}

// Query args for the batched event-group histogram. `eventGroupIds` accepts 1–10 ids.
//
// The id field name, not a `groupBy` param, selects the endpoint: the BE checks each id
// against the kind of thing the field promises, so an event-type id sent here returns a
// 400 naming the offending field rather than silently bucketing the wrong entity.
type GetBlueprintFamilyEventGroupDurationDistributionQueryParams = {
  eventGroupIds: string[]
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

const eventGroupDurationDistributionApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlueprintFamilyEventGroupDurationDistribution: builder.query<
      BlueprintFamilyEventGroupDurationDistributionApiItem[],
      GetBlueprintFamilyEventGroupDurationDistributionQueryParams
    >({
      // POST-with-body modeled as a query (read-only fan-out), mirroring the sibling
      // trends endpoints.
      query: ({ eventGroupIds, windowDays, ...scope }) => ({
        url: 'blueprint-families/duration-distribution/event-groups',
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { eventGroupIds, ...buildConventionalScopeBody(scope) },
      }),
      providesTags: (_result, _err, { eventGroupIds, windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `duration-distribution/event-groups/${[...eventGroupIds].sort().join(',')}/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyEventGroupDurationDistributionQuery } = eventGroupDurationDistributionApi
